import { supabase } from '../lib/supabase';

/**
 * Student Goals Service
 * Handles all goal-setting, milestone tracking, and achievement operations
 */

/**
 * Get all goals for the current user
 */
export const getUserGoals = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase?.from('student_goals')?.select(`
        *,
        play:plays(id, title),
        milestones:goal_milestones(*)
      `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user goals:', error);
    throw error;
  }
};

/**
 * Get active goals for the current user
 */
export const getActiveGoals = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase?.from('student_goals')?.select(`
        *,
        play:plays(id, title),
        milestones:goal_milestones(*)
      `)?.eq('user_id', user?.id)?.eq('status', 'active')?.order('target_date', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching active goals:', error);
    throw error;
  }
};

/**
 * Create a new goal
 */
export const createGoal = async (goalData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase?.from('student_goals')?.insert({
        user_id: user?.id,
        ...goalData
      })?.select()?.single();

    if (error) throw error;

    // Create default milestones based on goal type
    if (data?.id) {
      await createDefaultMilestones(data?.id, goalData?.goal_type, goalData?.target_value);
    }

    return data;
  } catch (error) {
    console.error('Error creating goal:', error);
    throw error;
  }
};

/**
 * Create default milestones for a goal
 */
const createDefaultMilestones = async (goalId, goalType, targetValue) => {
  const milestones = [];

  if (goalType === 'overall_accuracy' || goalType === 'play_specific') {
    const step = targetValue / 4;
    milestones?.push(
      { milestone_name: `Reach ${step?.toFixed(0)}% accuracy`, milestone_value: step },
      { milestone_name: `Reach ${(step * 2)?.toFixed(0)}% accuracy`, milestone_value: step * 2 },
      { milestone_name: `Reach ${(step * 3)?.toFixed(0)}% accuracy`, milestone_value: step * 3 },
      { milestone_name: `Reach target ${targetValue?.toFixed(0)}%`, milestone_value: targetValue }
    );
  } else if (goalType === 'weekly_practice') {
    const step = Math.ceil(targetValue / 4);
    milestones?.push(
      { milestone_name: `Complete ${step} sessions`, milestone_value: step },
      { milestone_name: `Complete ${step * 2} sessions`, milestone_value: step * 2 },
      { milestone_name: `Complete ${step * 3} sessions`, milestone_value: step * 3 },
      { milestone_name: `Reach target ${targetValue} sessions`, milestone_value: targetValue }
    );
  }

  if (milestones?.length > 0) {
    const { error } = await supabase?.from('goal_milestones')?.insert(milestones?.map(m => ({ ...m, goal_id: goalId })));

    if (error) console.error('Error creating milestones:', error);
  }
};

/**
 * Update goal status
 */
export const updateGoalStatus = async (goalId, status) => {
  try {
    const { data, error } = await supabase?.from('student_goals')?.update({ 
        status,
        completed_at: status === 'completed' ? new Date()?.toISOString() : null,
        updated_at: new Date()?.toISOString()
      })?.eq('id', goalId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating goal status:', error);
    throw error;
  }
};

/**
 * Delete a goal
 */
export const deleteGoal = async (goalId) => {
  try {
    const { error } = await supabase?.from('student_goals')?.delete()?.eq('id', goalId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting goal:', error);
    throw error;
  }
};

/**
 * Get practice streak for current user
 */
export const getPracticeStreak = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase?.from('practice_streaks')?.select('*')?.eq('user_id', user?.id)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    return data || { current_streak: 0, longest_streak: 0, total_practice_days: 0 };
  } catch (error) {
    console.error('Error fetching practice streak:', error);
    throw error;
  }
};

/**
 * Update practice streak
 */
export const updatePracticeStreak = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    const { error } = await supabase?.rpc('update_practice_streak', {
      p_user_id: user?.id
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error updating practice streak:', error);
    throw error;
  }
};

/**
 * Get achievement badges for current user
 */
export const getAchievementBadges = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase?.from('achievement_badges')?.select('*')?.eq('user_id', user?.id)?.order('earned_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching achievement badges:', error);
    throw error;
  }
};

/**
 * Award an achievement badge
 */
export const awardBadge = async (badgeType, badgeName, badgeDescription, goalId = null) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase?.rpc('award_achievement_badge', {
      p_user_id: user?.id,
      p_badge_type: badgeType,
      p_badge_name: badgeName,
      p_badge_description: badgeDescription,
      p_goal_id: goalId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error awarding badge:', error);
    throw error;
  }
};

/**
 * Get pronunciation analytics for goal tracking
 */
export const getGoalAnalytics = async (playId = null, startDate = null) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    let query = supabase?.from('pronunciation_analytics')?.select('*')?.eq('user_id', user?.id);

    if (playId) {
      query = query?.eq('play_id', playId);
    }

    if (startDate) {
      query = query?.gte('created_at', startDate);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching goal analytics:', error);
    throw error;
  }
};

/**
 * Get goal progress summary
 */
export const getGoalProgressSummary = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('No authenticated user');

    // Get all goals with milestones
    const { data: goals, error: goalsError } = await supabase?.from('student_goals')?.select(`
        *,
        play:plays(title),
        milestones:goal_milestones(*)
      `)?.eq('user_id', user?.id);

    if (goalsError) throw goalsError;

    // Calculate summary statistics
    const activeGoals = goals?.filter(g => g?.status === 'active')?.length || 0;
    const completedGoals = goals?.filter(g => g?.status === 'completed')?.length || 0;
    const totalMilestones = goals?.reduce((sum, g) => sum + (g?.milestones?.length || 0), 0) || 0;
    const achievedMilestones = goals?.reduce((sum, g) => 
      sum + (g?.milestones?.filter(m => m?.is_achieved)?.length || 0), 0) || 0;

    return {
      activeGoals,
      completedGoals,
      totalGoals: goals?.length || 0,
      totalMilestones,
      achievedMilestones,
      milestoneProgress: totalMilestones > 0 ? (achievedMilestones / totalMilestones * 100)?.toFixed(1) : 0
    };
  } catch (error) {
    console.error('Error fetching goal progress summary:', error);
    throw error;
  }
};

/**
 * Get leaderboard data
 */
export const getLeaderboard = async (limit = 10) => {
  try {
    const { data, error } = await supabase?.from('pronunciation_analytics')?.select(`
        user_id,
        user:user_profiles(full_name),
        accuracy_score
      `)?.order('accuracy_score', { ascending: false })?.limit(limit);

    if (error) throw error;

    // Group by user and calculate average
    const leaderboard = {};
    data?.forEach(record => {
      if (!leaderboard?.[record?.user_id]) {
        leaderboard[record.user_id] = {
          user_id: record?.user_id,
          full_name: record?.user?.full_name || 'Anonymous',
          total_score: 0,
          count: 0
        };
      }
      leaderboard[record.user_id].total_score += record?.accuracy_score;
      leaderboard[record.user_id].count += 1;
    });

    const sortedLeaderboard = Object.values(leaderboard)?.map(entry => ({
        ...entry,
        average_score: (entry?.total_score / entry?.count)?.toFixed(2)
      }))?.sort((a, b) => b?.average_score - a?.average_score)?.slice(0, limit);

    return sortedLeaderboard;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
};

export default {
  getUserGoals,
  getActiveGoals,
  createGoal,
  updateGoalStatus,
  deleteGoal,
  getPracticeStreak,
  updatePracticeStreak,
  getAchievementBadges,
  awardBadge,
  getGoalAnalytics,
  getGoalProgressSummary,
  getLeaderboard
};